/**
 * components/dashboard/DashboardOnboardingCard.tsx
 *
 * Profile setup nudge shown on the Dashboard to users who haven't configured
 * their logo and/or stamp yet.
 *
 * Design principles:
 *   - Distinct blue-tinted background (bg-primary-50) to signal temporariness.
 *   - Title → actions → explanation ordering (scan → act → understand).
 *   - Progress counter "X sur 2" next to the title for instant progress feedback.
 *   - Each action is a full-width clickable row (no separate "Configurer" buttons).
 *   - Completed items are visually struck-through and non-interactive.
 *   - ChevronRight on incomplete rows signals navigability without text clutter.
 *   - min-h-[44px] on each row for accessible touch targets on mobile.
 *   - Animated entrance (slide-down + fade) and animated exit on dismiss.
 *
 * Auto-dismissal when both items are completed is handled in useSettingsProfile.saveProfile.
 *
 * Updated Apr 2026: full redesign - blue background, progress badge, cliquable rows,
 *   no duplicate CTA buttons, enter/exit animations, accessible close button.
 * Updated Apr 2026: unmount gate simplified to !shouldShow so the component unmounts
 *   cleanly after refreshProfile resolves; isExiting is only used for the animation class.
 */

import { useState } from 'react';
import { X, CheckCircle2, Circle, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboardingDismissal } from '../../hooks/useOnboardingDismissal';

export default function DashboardOnboardingCard() {
  const { userProfile } = useAuth();
  const { shouldShow, dismissOnboarding, dismissing } = useOnboardingDismissal();
  const [isExiting, setIsExiting] = useState(false);

  if (!shouldShow) return null;
  if (!userProfile) return null;

  const hasLogo = !!userProfile.logoUrl;
  const hasStamp = !!userProfile.stampTitle;
  const completedCount = (hasLogo ? 1 : 0) + (hasStamp ? 1 : 0);

  const handleDismiss = () => {
    if (dismissing || isExiting) return;
    setIsExiting(true);
  };

  const handleAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'onboarding-exit') {
      dismissOnboarding();
    }
  };

  return (
    <div
      onAnimationEnd={handleAnimationEnd}
      className={`bg-primary-50 border border-primary-200 rounded-2xl shadow-soft ${
        isExiting ? 'animate-onboarding-exit' : 'animate-onboarding-enter'
      }`}
    >
      <div className="p-5 sm:p-6">

        {/* Header: title + progress badge + close */}
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-neutral-900 leading-snug">
                Bienvenue, pensez à personnaliser votre espace
              </h3>
              <span className="text-xs font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full flex-shrink-0">
                {completedCount} sur 2
              </span>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            disabled={dismissing || isExiting}
            aria-label="Fermer le message de bienvenue"
            className="flex-shrink-0 p-2 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-white/70 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Action checklist */}
        <div className="space-y-0.5 mb-4">

          {/* Logo row */}
          {hasLogo ? (
            <div className="flex items-center gap-3 py-2.5 px-3 min-h-[44px] rounded-xl">
              <CheckCircle2 className="w-5 h-5 text-success-500 flex-shrink-0" />
              <span className="text-sm font-medium text-neutral-400 line-through flex-1">
                Ajouter mon logo
              </span>
            </div>
          ) : (
            <Link
              to="/settings#profile-logo"
              aria-label="Ajouter mon logo - étape non complétée"
              className="flex items-center gap-3 py-2.5 px-3 min-h-[44px] rounded-xl hover:bg-white/70 active:bg-white/90 transition-colors group"
            >
              <Circle className="w-5 h-5 text-neutral-300 flex-shrink-0 group-hover:text-primary-300 transition-colors" />
              <span className="text-sm font-medium text-neutral-800 flex-1">
                Ajouter mon logo
              </span>
              <ChevronRight className="w-4 h-4 text-neutral-300 group-hover:text-neutral-500 transition-colors flex-shrink-0" />
            </Link>
          )}

          {/* Stamp row */}
          {hasStamp ? (
            <div className="flex items-center gap-3 py-2.5 px-3 min-h-[44px] rounded-xl">
              <CheckCircle2 className="w-5 h-5 text-success-500 flex-shrink-0" />
              <span className="text-sm font-medium text-neutral-400 line-through flex-1">
                Configurer mon tampon
              </span>
            </div>
          ) : (
            <Link
              to="/settings#stamp"
              aria-label="Configurer mon tampon - étape non complétée"
              className="flex items-center gap-3 py-2.5 px-3 min-h-[44px] rounded-xl hover:bg-white/70 active:bg-white/90 transition-colors group"
            >
              <Circle className="w-5 h-5 text-neutral-300 flex-shrink-0 group-hover:text-primary-300 transition-colors" />
              <span className="text-sm font-medium text-neutral-800 flex-1">
                Configurer mon tampon
              </span>
              <ChevronRight className="w-4 h-4 text-neutral-300 group-hover:text-neutral-500 transition-colors flex-shrink-0" />
            </Link>
          )}
        </div>

        {/* Explanation */}
        <p className="text-xs text-neutral-500 leading-relaxed">
          Votre logo et votre tampon apparaîtront dans les formulaires envoyés à vos clients et sur les PDF générés.
        </p>

      </div>
    </div>
  );
}
